// Tile face art — ink-only SVGs (transparent background) from the public-domain
// riichi-mahjong-tiles set (see assets/tiles/CREDITS.md), composited onto our
// own procedural tile body (TileRenderer's rounded rect + gradient + border)
// rather than replacing it, so the wood theme still governs the tile itself.
// Explicit per-file imports rather than `import.meta.glob` — this is the only
// image-asset usage in the monorepo, so keeping it grep-able beats terse.

import type { Honor, Rank, Suit } from '../game/types.js';

import man1 from '../assets/tiles/Man1.svg';
import man2 from '../assets/tiles/Man2.svg';
import man3 from '../assets/tiles/Man3.svg';
import man4 from '../assets/tiles/Man4.svg';
import man5 from '../assets/tiles/Man5.svg';
import man6 from '../assets/tiles/Man6.svg';
import man7 from '../assets/tiles/Man7.svg';
import man8 from '../assets/tiles/Man8.svg';
import man9 from '../assets/tiles/Man9.svg';
import pin1 from '../assets/tiles/Pin1.svg';
import pin2 from '../assets/tiles/Pin2.svg';
import pin3 from '../assets/tiles/Pin3.svg';
import pin4 from '../assets/tiles/Pin4.svg';
import pin5 from '../assets/tiles/Pin5.svg';
import pin6 from '../assets/tiles/Pin6.svg';
import pin7 from '../assets/tiles/Pin7.svg';
import pin8 from '../assets/tiles/Pin8.svg';
import pin9 from '../assets/tiles/Pin9.svg';
import sou1 from '../assets/tiles/Sou1.svg';
import sou2 from '../assets/tiles/Sou2.svg';
import sou3 from '../assets/tiles/Sou3.svg';
import sou4 from '../assets/tiles/Sou4.svg';
import sou5 from '../assets/tiles/Sou5.svg';
import sou6 from '../assets/tiles/Sou6.svg';
import sou7 from '../assets/tiles/Sou7.svg';
import sou8 from '../assets/tiles/Sou8.svg';
import sou9 from '../assets/tiles/Sou9.svg';
import ton from '../assets/tiles/Ton.svg';
import nan from '../assets/tiles/Nan.svg';
import shaa from '../assets/tiles/Shaa.svg';
import pei from '../assets/tiles/Pei.svg';
import haku from '../assets/tiles/Haku.svg';
import hatsu from '../assets/tiles/Hatsu.svg';
import chun from '../assets/tiles/Chun.svg';

const MAN_URLS: Record<Rank, string> = { 1: man1, 2: man2, 3: man3, 4: man4, 5: man5, 6: man6, 7: man7, 8: man8, 9: man9 };
const PIN_URLS: Record<Rank, string> = { 1: pin1, 2: pin2, 3: pin3, 4: pin4, 5: pin5, 6: pin6, 7: pin7, 8: pin8, 9: pin9 };
const SOU_URLS: Record<Rank, string> = { 1: sou1, 2: sou2, 3: sou3, 4: sou4, 5: sou5, 6: sou6, 7: sou7, 8: sou8, 9: sou9 };
const SUIT_URLS: Record<Suit, Record<Rank, string>> = { man: MAN_URLS, pin: PIN_URLS, sou: SOU_URLS };

const HONOR_URLS: Record<Honor, string> = {
  east: ton,
  south: nan,
  west: shaa,
  north: pei,
  white: haku,
  green: hatsu,
  red: chun,
};

const cache = new Map<string, HTMLImageElement>();

function loadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      cache.set(url, img);
      resolve();
    };
    img.onerror = () => reject(new Error(`Failed to load tile sprite: ${url}`));
    img.src = url;
  });
}

/** Decode every tile face once, up front — call before the first `drawTileFace`. */
export function preloadTileSprites(): Promise<void> {
  const urls = [...Object.values(MAN_URLS), ...Object.values(PIN_URLS), ...Object.values(SOU_URLS), ...Object.values(HONOR_URLS)];
  return Promise.all(urls.map(loadImage)).then(() => undefined);
}

export function getSuitSprite(suit: Suit, rank: Rank): HTMLImageElement | undefined {
  return cache.get(SUIT_URLS[suit][rank]);
}

export function getHonorSprite(honor: Honor): HTMLImageElement | undefined {
  return cache.get(HONOR_URLS[honor]);
}
